import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { MessageCircle, Bell, Lock, Inbox, Radio } from 'lucide-react';
import logoImage from '../assets/f736bb54a1ccb5a9a30d8b8ec9d3974d4b7ef9d6.png';
import logoImageWhite from '../assets/fac7d53f9fcb6f491ee2fb6641c18821c53c606e.png';
import goldIcon from '../assets/gold icon.png';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { ActivityStatus } from './ui/ActivityStatus';
import { useMessaging } from './MessagingContext';
import { useScreenReady } from '../contexts/ScreenTransitionContext';
import { useUserData } from './UserDataContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { ParallaxBackground } from './utils/parallax';
import { useTheme } from './ThemeProvider';
import { useNotificationBadges } from './NotificationContext';
import { getDbSafe } from '../services/firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';
import { formatTimeAgo } from './utils/timeFormat';
import { FlagImage } from './utils/countryFlags';
import RankBadge from './RankBadge';
import { PressableCard } from './ui/PressableCard';
import { RoomsFeed } from './RoomsFeed';
import { ScreenLoader } from './ScreenLoader';
import { UnreadBadge } from './ui/UnreadBadge';

interface Chat {
  id: number;
  name: string;
  avatar: string;
  lastMessage: string;
  time: string;
  unread: number;
  online: boolean;
  username?: string;
  country?: string;
  countryFlag?: string;
  rank?: string;
}

export function ChatScreen() {
  const { chats: allChats, chatsLoaded, messageRequests: allRequests, unreadCounts, latestMessages, totalUnread } = useMessaging();
  useScreenReady(chatsLoaded);
  const { filterBlocked } = useContentFilter();
  // 🚫 Blocked users must not surface in the chat list or in message requests.
  const chats = filterBlocked(allChats);
  const messageRequests = filterBlocked(allRequests);
  // Badge counts what's actually shown — a request from a blocked user is hidden,
  // so counting it would leave an unclearable badge.
  const requestCount = messageRequests.length;
  // What the Messages pill shows while the user is on Rooms: unread
  // conversations plus waiting requests, since both sit under that section.
  const messagesBadge = (totalUnread || 0) + requestCount;
  const { userProfile, getFriendsCount } = useUserData();
  const { unreadCount } = useNotificationBadges();
  const { theme } = useTheme();
  const friendsCount = getFriendsCount();
  const dmPrivacy = userProfile.dmPrivacy || 'friends';
  // Two levels: Rooms | Messages on the outside, and inside Messages a
  // General | Requests filter. Requests is no longer a peer of Messages, but
  // nothing about it was removed — it just moved down a level. The inner tab is
  // "General" so the two levels do not both read "Messages".
  const [activeSection, setActiveSection] = useState<'rooms' | 'messages'>('rooms');

  // The guide walks Rooms then Direct Messages, and each is a section of this
  // screen rather than a route, so it asks for one by event.
  useEffect(() => {
    const onSection = (e: Event) => {
      const which = (e as CustomEvent).detail;
      if (which === 'rooms' || which === 'messages') setActiveSection(which);
    };
    window.addEventListener('guide:chatSection', onSection);
    return () => window.removeEventListener('guide:chatSection', onSection);
  }, []);
  const [activeTab, setActiveTab] = useState<'chats' | 'requests'>('chats');

  console.log('[ChatScreen] Rendering with', chats.length, 'chats');

  const handleChatClick = (chat: any) => {
    console.log('[ChatScreen] Chat clicked:', chat.name);
    const friendData = {
      ...chat,
      id: chat.friendId,
      chatId: chat.id,
    };
    window.dispatchEvent(new CustomEvent('navigateToDirectMessage', { detail: friendData }));
  };

  const handleRequestClick = (chat: any) => {
    const friendData = {
      ...chat,
      id: chat.friendId,
      chatId: chat.id,
      isRequest: true,
    };
    window.dispatchEvent(new CustomEvent('navigateToDirectMessage', { detail: friendData }));
  };

  // Determine the empty state message
  const getEmptyStateMessage = () => {
    if (dmPrivacy === 'friends' && friendsCount === 0) {
      return 'Add more friends to send yaps to';
    }
    return 'Start connecting with friends! Your conversations will appear here once you send or receive messages.';
  };

  // A cold load — a refresh or the app opening here — has no route transition to
  // cover it, and this screen renders its chat list regardless of readiness. That
  // showed "no chats yet" to someone who simply has chats that had not arrived.
  // chatsLoaded is what separates empty from not-yet-loaded.
  if (!chatsLoaded) return <ScreenLoader />;
  return (
    <div
      className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative overflow-hidden"
      style={{ height: '100dvh' }}
/* h-screen + inline 100dvh, not h-dvh: that utility has no rule in the
        committed pre-compiled index.css, so the root collapsed to height:auto
        and the absolute inset-0 pattern layer covered only as far as the
        content reached — below it the flat background showed through, black in
        dark mode. h-screen gives 100vh from the stylesheet; the inline value
        upgrades to the dynamic viewport where that is supported. */
    >
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10 pb-20">
        {/* Chat List Header */}
        <div
          className="pb-4 px-6 border-b border-black/10 dark:border-white/10 bg-[#e9dbae]/70 dark:bg-[#0D0D0D]/80 backdrop-blur-lg"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}
        >
          <div className="flex items-center justify-center relative min-h-[40px]">
            {/* Logo and Notifications on the left */}
            <div className="absolute left-0 flex items-center gap-2">
              <img src={theme === 'dark' ? (logoImageWhite as any) : (logoImage as any)} alt="AfroYap" className="h-6 w-auto" />
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'notifications' }))}
                className="relative p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              >
                <Bell className="w-5 h-5 text-black dark:text-white" />
                {/* Inline offsets: -left-2 is not in the committed pre-compiled
                    index.css, so this badge had no horizontal offset at all. */}
                {unreadCount > 0 && (
                  <UnreadBadge count={unreadCount} style={{ top: -8, right: -8 }} />
                )}
              </motion.button>
            </div>

            <h1 className="text-3xl text-black dark:text-white" style={{ fontFamily: "'NarnialOne', 'Narniaone', 'NARNIAONE', 'Poppins', sans-serif" }}>
              Chat
            </h1>

            <div className="absolute right-0">
              {/* Market / Gold Display */}
              <motion.button
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-1.5 bg-black/5 dark:bg-white/5 px-3 py-1.5 rounded-full border border-black/10 dark:border-white/10 backdrop-blur-sm opacity-50 cursor-not-allowed"
              >
                <img src={goldIcon as any} alt="Gold" className="h-4 w-auto object-contain" />
                <span className="text-black dark:text-white text-xs font-bold">{Number(userProfile.goldBalance || 0).toLocaleString()}</span>
              </motion.button>
            </div>
          </div>

          {/* Rooms / Messages — the top level. Rooms is shown first. */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setActiveSection('rooms')}
              className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${
                activeSection === 'rooms'
                  ? 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-md'
                  : 'bg-black/5 dark:bg-white/5 text-black/60 dark:text-white/60 hover:bg-black/10 dark:hover:bg-white/10'
              }`}
            >
              <Radio className="w-4 h-4" />
              Rooms
            </button>
            <button
              onClick={() => setActiveSection('messages')}
              className={`relative flex-1 py-2 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${
                activeSection === 'messages'
                  ? 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-md'
                  : 'bg-black/5 dark:bg-white/5 text-black/60 dark:text-white/60 hover:bg-black/10 dark:hover:bg-white/10'
              }`}
            >
              <MessageCircle className="w-4 h-4" />
              Direct Messages
              {/* Rides on the section pill rather than inside it: unread DMs and
                  waiting requests both live under Direct Messages, so while the user is
                  looking at Rooms this is the only thing that can show either.
                  Counted together for that reason — one number for "there is
                  something here", which the sub-tabs then break down.

                  Offsets are inline, not -top-2/-right-2 classes: index.css is
                  committed pre-compiled Tailwind and does not contain every
                  offset utility (-left-2 is missing, which is why the bell badge
                  above sits in the wrong place), and a class it lacks renders
                  nothing at all. */}
              {messagesBadge > 0 && activeSection !== 'messages' && (
                <UnreadBadge count={messagesBadge} style={{ top: -8, right: -6 }} />
              )}
            </button>
          </div>

          {/* Messages / Requests — unchanged, one level down. */}
          {activeSection === 'messages' && (
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setActiveTab('chats')}
              className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all ${
                activeTab === 'chats'
                  ? 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-md'
                  : 'bg-black/5 dark:bg-white/5 text-black/60 dark:text-white/60 hover:bg-black/10 dark:hover:bg-white/10'
              }`}
            >
              {/* "Primary", as the counterpart to Requests: these are the
                  conversations already accepted, those are the ones waiting on
                  a decision. It also cannot collide with the row above, which
                  reads "Direct Messages" — the two rows used to both say
                  "Messages" and neither told you which one you were choosing. */}
              Primary
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`relative flex-1 py-2 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${
                activeTab === 'requests'
                  ? 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-md'
                  : 'bg-black/5 dark:bg-white/5 text-black/60 dark:text-white/60 hover:bg-black/10 dark:hover:bg-white/10'
              }`}
            >
              <Inbox className="w-4 h-4" />
              Requests
              {requestCount > 0 && (
                <UnreadBadge count={requestCount} style={{ top: -8, right: -6 }} />
              )}
            </button>
          </div>
          )}
        </div>

        {/* Rooms feed, or the existing chat/request list */}
        {activeSection === 'rooms' ? (
          <RoomsFeed />
        ) : (
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pb-24">
          <AnimatePresence mode="wait">
            {activeTab === 'chats' ? (
              <motion.div
                key="chats"
                data-tour="guide-chats"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.15 }}
              >
                {chats.length === 0 ? (
                  /* Empty State */
                  <div className="flex flex-col items-center justify-center h-full px-6 py-12">
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className="flex flex-col items-center text-center"
                    >
                      <div className="w-24 h-24 rounded-full bg-linear-to-br from-[#F39C12]/20 to-[#E74C3C]/20 dark:from-[#4478ea]/20 dark:to-[#2d1ef1]/20 flex items-center justify-center mb-6">
                        <MessageCircle className="w-12 h-12 text-[#F39C12] dark:text-[#4478ea]" />
                      </div>
                      <h2 className="text-2xl text-black dark:text-white mb-3">
                        No Chats Yet
                      </h2>
                      <p className="text-[#666666] dark:text-[#a0a0a0] max-w-sm mb-6">
                        {getEmptyStateMessage()}
                      </p>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'discover' }));
                        }}
                        className="px-6 py-3 rounded-2xl bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-lg"
                      >
                        Find Friends
                      </motion.button>
                    </motion.div>
                  </div>
                ) : (
                  /* Chat List */
                  <div className="divide-y divide-black/10 dark:divide-white/10">
                    {chats.map((chat) => (
                      <PressableCard
                        key={chat.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        whileHover={{ x: 5 }}
                        onClick={() => handleChatClick(chat)}
                        className="p-4 flex items-center gap-4 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                      >
                        <div className="relative">
                          <Avatar className="w-14 h-14 border-2 border-[#4478ea]">
                            <AvatarImage src={chat.avatar} />
                            <AvatarFallback>{chat.name[0]}</AvatarFallback>
                          </Avatar>
                          <ActivityStatus userId={(chat as any).friendId} variant="dot" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <h3 className="text-black dark:text-white truncate font-semibold">{chat.name}</h3>
                              <RankBadge rank={chat.rank} size={18} />
                              {(chat.countryFlag || chat.country) && (
                                <FlagImage
                                  countryName={(chat.countryFlag || chat.country) as string}
                                  size="w80"
                                  className="w-4 h-auto rounded-sm object-cover"
                                />
                              )}
                            </div>
                            <span className="text-sm text-[#666666] dark:text-[#a0a0a0] shrink-0 font-medium">{formatTimeAgo(chat.time)}</span>
                          </div>
                          <p className={`text-sm truncate ${unreadCounts[chat.id] > 0 ? 'text-black dark:text-white font-medium' : 'text-[#666666] dark:text-[#a0a0a0]'}`}>{chat.lastMessage || latestMessages[chat.id] || 'No messages yet'}</p>
                          <ActivityStatus userId={(chat as any).friendId} style={{ fontSize: 11, marginTop: 2 }} />
                        </div>
                        {unreadCounts[chat.id] > 0 && (
                          <div className="w-6 h-6 rounded-full bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] flex items-center justify-center shrink-0 shadow-sm">
                            <span className="text-xs font-bold text-white shadow-sm">{unreadCounts[chat.id]}</span>
                          </div>
                        )}
                      </PressableCard>
                    ))}
                  </div>
                )}
              </motion.div>
            ) : (
              /* Requests Tab */
              <motion.div
                key="requests"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.15 }}
              >
                {messageRequests.length === 0 ? (
                  <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
                    <div className="w-20 h-20 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center mb-5">
                      <Inbox className="w-10 h-10 text-black/30 dark:text-white/30" />
                    </div>
                    <h2 className="text-xl text-black dark:text-white mb-2 font-bold">No Requests</h2>
                    <p className="text-sm text-[#666666] dark:text-[#a0a0a0] max-w-xs">
                      When someone who isn't your friend messages you, it will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-black/10 dark:divide-white/10">
                    {/* Privacy notice */}
                    <div className="px-4 py-2.5 flex items-center gap-2 bg-[#4478ea]/5 dark:bg-[#4478ea]/10">
                      <Lock className="w-3.5 h-3.5 text-[#4478ea] shrink-0" />
                      <p className="text-xs text-[#4478ea] font-medium">Message content is hidden until you open the conversation</p>
                    </div>
                    {messageRequests.map((req: any) => (
                      <PressableCard
                        key={req.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        whileHover={{ x: 5 }}
                        onClick={() => handleRequestClick(req)}
                        className="p-4 flex items-center gap-4 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                      >
                        <div className="relative">
                          <Avatar className="w-14 h-14 border-2 border-[#F39C12] dark:border-[#4478ea]">
                            <AvatarImage src={req.avatar} />
                            <AvatarFallback>{req.name[0]}</AvatarFallback>
                          </Avatar>
                          {/* Request indicator dot */}
                          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-[#F39C12] dark:bg-[#4478ea] rounded-full border-2 border-white dark:border-[#0D0D0D] flex items-center justify-center">
                            <Lock className="w-2 h-2 text-white" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <h3 className="text-black dark:text-white truncate font-semibold">{req.name}</h3>
                              <RankBadge rank={req.rank} size={18} />
                              {(req.countryFlag || req.country) && (
                                <FlagImage
                                  countryName={(req.countryFlag || req.country) as string}
                                  size="w80"
                                  className="w-4 h-auto rounded-sm object-cover"
                                />
                              )}
                            </div>
                            <span className="text-sm text-[#666666] dark:text-[#a0a0a0] shrink-0 font-medium">{formatTimeAgo(req.time)}</span>
                          </div>
                          {/* Hidden preview */}
                          <div className="flex items-center gap-1.5">
                            <Lock className="w-3 h-3 text-[#999] shrink-0" />
                            <p className="text-sm text-[#999999] dark:text-[#666666] italic">Tap to view message</p>
                          </div>
                        </div>
                        {/* New request badge */}
                        <div className="w-2.5 h-2.5 rounded-full bg-[#F39C12] dark:bg-[#4478ea] shrink-0" />
                      </PressableCard>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        )}
      </div>
    </div>
  );
}
